import api from './api';

/**
 * EDI Orders (cms.edi_orders) — the maintenance page's full-record fetch + conditional
 * edits. The grid is the read-only /tablemaint engine (key 'edi-orders'); the drawer
 * pulls the full denormalized record here (the grid can't carry all of it), and writes
 * only the small set of edits the business rules allow.
 */
export interface EdiOrderRecord {
  recno: number;
  pronumber: number;
  container: string;
  scac: string;
  /** edi_scacmap recno backing SCAC — for the "Map SCAC" action (0 = none). */
  mappedscac: number;
  movetype: string;
  po: string;
  bol: string;
  booking: string;
  billtorefno: string;
  sealnumber: string;
  voyage: string;
  hazmat: string;
  hazmatinfo: string;
  rateinfo: string;
  template: string;
  comments: string;
  nte: string;
  lad: string;
  shippercode: string;
  sapordernumber: string;
  editype: string;
  deletereason: string;
  deleted: string;
  acceptreject: string;
  acceptedby: string;
  acceptdatetime: string;
  edi990sentdatetime: string;
  acceptrejectinfo: string;
  billtoname: string;
  processdatetime: string;
  pickupname: string;
  deliveryname: string;
  terminatename: string;
  // Location mapping: edi_locations recno (0 = none), EDI-supplied name, and the
  // ACTIVE mapped CMS location name (blank when unmapped/deleted), per slot.
  pickupcode: number;
  deliverycode: number;
  terminatecode: number;
  pickupediname: string;
  deliveryediname: string;
  terminateediname: string;
  pickupmapname: string;
  deliverymapname: string;
  terminatemapname: string;
  sizetype: string;
  /** edi_equipmentmap recno backing Size/Type — for the "Map Size/Type" action. */
  equipmentmaprecno: number;
  vesselvoyage: string;
  lbskgs: string;
  portoforigin: string;
  portofdischarge: string;
  // Raw EDI UN/LOCODE + mapped CMS port name (blank if unmapped) — for the "Map Port" action.
  portoforiginunlocode: string;
  portoforiginname: string;
  portofdischargeunlocode: string;
  portofdischargename: string;
  brokername: string;
  customername: string;
  /** Each party's OWN soft-delete flag ('Y' / ''), so the drawer can flag an order still
   *  pointing at a since-deleted bill-to / customer / broker. */
  billtodeleted: string;
  custdeleted: string;
  brokerdeleted: string;
  /** Legacy EDI file path (read-only reference) + the cmsdata-nas blob path the raw file is
   *  fetched from (blank when there's no file to view/download). */
  edifilename: string;
  nas_edifilename: string;
  [k: string]: any;
}

export interface EdiOrderPatch {
  clear_pronumber?: boolean;
  movetype?: string;
  clear_template?: boolean;
  deleted?: string;
  deletereason?: string;
  clear_accept_reject?: boolean;
}

export async function getEdiOrder(recno: number): Promise<EdiOrderRecord> {
  const res = await api.get(`/edi-orders/${recno}`);
  return res.data as EdiOrderRecord;
}

export async function updateEdiOrder(recno: number, patch: EdiOrderPatch): Promise<void> {
  await api.put(`/edi-orders/${recno}`, patch);
}

/** The raw inbound EDI file for this order (from blob storage), as a Blob. `download=false`
 *  (view) streams text/plain so it renders inline; `download=true` streams as an attachment.
 *  Rejects (404) when the order has no file / the blob can't be read. */
async function fetchEdiOrderFile(recno: number, download: boolean): Promise<Blob> {
  const res = await api.get(`/edi-orders/${recno}/file`, {
    params: { download }, responseType: 'blob',
  });
  return res.data as Blob;
}

export const previewEdiOrderFile = (recno: number): Promise<Blob> => fetchEdiOrderFile(recno, false);
export const downloadEdiOrderFile = (recno: number): Promise<Blob> => fetchEdiOrderFile(recno, true);

/** Filename to suggest when saving the EDI file — the last segment of its nas path. */
export const ediFileName = (nasEdifilename: string): string =>
  (nasEdifilename || '').split('/').filter(Boolean).pop() || 'edi_file.txt';
